import React from 'react';
import { motion } from 'framer-motion';
import { LogIn, ArrowRight, ShieldCheck, Zap, LayoutDashboard } from 'lucide-react';

interface LandingPageProps {
    onLogin: () => void;
}

export const LandingPage = ({ onLogin }: LandingPageProps) => {
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col font-sans transition-colors duration-300">
            {/* Hero Section */}
            <div className="flex-1 flex flex-col items-center justify-center p-6 relative overflow-hidden">

                {/* Abstract Background Shapes */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                    <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-siemens-petrol/5 blur-3xl" />
                    <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-siemens-deep-blue/5 dark:bg-siemens-petrol/10 blur-3xl" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative z-10 max-w-4xl w-full text-center"
                >
                    {/* Logo / Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm mb-8">
                        <span className="flex h-2 w-2 rounded-full bg-siemens-petrol animate-pulse"></span>
                        <span className="text-xs font-semibold text-siemens-deep-blue dark:text-slate-300 tracking-wide uppercase">
                            Siemens ZPS Innovation
                        </span>
                    </div>

                    {/* Main Headline */}
                    <h1 className="text-5xl md:text-7xl font-black text-siemens-deep-blue dark:text-white tracking-tight mb-6 leading-tight">
                        Prioritize with <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-siemens-petrol to-cyan-500">
                            Intelligence
                        </span>
                    </h1>

                    {/* Subheadline */}
                    <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
                        The strategic portfolio intelligence platform for managing epics, tracking value, and driving innovation across Siemens ZPS.
                    </p>

                    {/* Login Button */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={onLogin}
                        className="group relative inline-flex items-center gap-3 px-8 py-4 bg-siemens-deep-blue dark:bg-white text-white dark:text-siemens-deep-blue rounded-xl font-bold text-lg shadow-xl shadow-siemens-deep-blue/20 dark:shadow-white/10 hover:shadow-2xl transition-all duration-300"
                    >
                        <LogIn className="w-5 h-5" />
                        <span>Sign In with Microsoft</span>
                        <ArrowRight className="w-5 h-5 opacity-70 group-hover:translate-x-1 transition-transform" />
                    </motion.button>


                </motion.div>

                {/* Feature Grid */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="relative z-10 mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full"
                >
                    <FeatureCard
                        icon={<LayoutDashboard className="w-6 h-6 text-siemens-petrol" />}
                        title="Strategic Alignment"
                        description="Visualize and align epics with strategic goals using WSJF scoring and matrix views."
                    />
                    <FeatureCard
                        icon={<Zap className="w-6 h-6 text-amber-500" />}
                        title="Real-time Analytics"
                        description="Track portfolio health, cycle times, and value delivery with dynamic dashboards."
                    />
                    <FeatureCard
                        icon={<ShieldCheck className="w-6 h-6 text-emerald-500" />}
                        title="Enterprise Secure"
                        description="Built with Azure AD authentication and RBAC for secure, matrix-based access control."
                    />
                </motion.div>
            </div>

            {/* Footer */}
            <div className="py-6 px-6 text-center text-sm text-slate-400 z-10">
                <p>© {new Date().getFullYear()} Siemens AG. Internal Use Only.</p>
            </div>
        </div>
    );
};

// Simple Feature Card Component
interface FeatureCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const FeatureCard = ({ icon, title, description }: FeatureCardProps) => (
    <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
        <div className="w-12 h-12 rounded-lg bg-slate-50 dark:bg-slate-700/50 flex items-center justify-center mb-4">
            {icon}
        </div>
        <h3 className="text-lg font-bold text-siemens-deep-blue dark:text-white mb-2">{title}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{description}</p>
    </div>
);

export default LandingPage;
